import { useMutation } from '@tanstack/react-query'
import { conversationService } from 'v2/shared/services/conversations-service'
import { usePhoneStore } from 'v2/state/phone'
import type {
  HoldCallPayload,
  MonitorPayload,
  CoachPayload,
  BargePayload,
  ConversationStatus,
} from 'v2/types/api/conversations'
import { MonitorStates, type RecordingPayload } from 'v2/types/call'
import { executionService } from 'v2/shared/services/execution-service'
import { primaryEventBus, PrimaryEvents } from 'v2/utils/event-bus/primary'
import { conversationMutationKeys } from 'v2/shared/utils/query-key-management'
import { errorToast } from 'v2/utils/toasts'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import type { AxiosError } from 'axios'
import { useConversationMutations } from './conversations'

const RaceConditionErrorString = 'Call Conference Race Condition Error - '

export const throwOnNoCalls = (e: string, throwIfNoParticipant: boolean): void => {
  const store = usePhoneStore.getState()
  const callActive = store.activeCall !== null && store.isCallActive
  if (!callActive) throw new Error(`${RaceConditionErrorString}${e}`)

  if (throwIfNoParticipant) {
    const participant = store.participants?.length > 0
    if (!participant) throw new Error(`${RaceConditionErrorString}No participant in progress`)
  }
}

export function useHoldCallMutation() {
  const setOnHold = usePhoneStore((state) => state.setOnHold)

  return useMutation({
    mutationFn: ({ payload, callUUID }: { payload: HoldCallPayload; callUUID: string }) =>
      conversationService.holdCall(payload, callUUID),
    onSuccess: (_, { payload }) => {
      // Update the phone store with the hold status
      setOnHold(payload.hold)
    },
  })
}

export function useToggleRecordingMutation() {
  const setIsRecordingPaused = usePhoneStore((state) => state.setIsRecordingPaused)

  return useMutation({
    mutationFn: ({ payload, callUUID }: { payload: RecordingPayload; callUUID: string }) =>
      conversationService.toggleRecording(payload, callUUID),
    onSuccess: (_, { payload }) => {
      setIsRecordingPaused(payload.pauseRecording)
    },
  })
}

export function useMonitorMutations() {
  const setMonitoringState = usePhoneStore((state) => state.setMonitoringState)
  const setIsMonitorInitiator = usePhoneStore((state) => state.setIsMonitorInitiator)
  const { updateActiveConversation, removeActiveConversation } = useConversationMutations()

  const startMonitoring = useMutation({
    mutationKey: conversationMutationKeys().startMonitoring,
    mutationFn: (payload: MonitorPayload) => conversationService.startMonitoring(payload),
    onSuccess: (_, { state }) => {
      setMonitoringState(state)
      setIsMonitorInitiator(true)
    },
    // This has to be handled from BE via MONITOR_FAILURE event
    // onError: (e) => {
    //   const errorMessage = getErrorText(e as AxiosError)
    //   errorToast({
    //     title: 'Failed to start monitoring',
    //     description: errorMessage,
    //   })
    // },
  })

  const startCoaching = useMutation({
    mutationFn: (payload: CoachPayload) => conversationService.startCoaching(payload),
    onSuccess: (_, { conversation_id }) => {
      setMonitoringState(MonitorStates.COACH)
      updateActiveConversation({
        conversationNumber: conversation_id,
        data: { agent_secondary_conversation_state: MonitorStates.COACH },
      })
    },
    onError: (e) => {
      const errorMessage = getErrorText(e as AxiosError)
      errorToast({
        title: 'Failed to start coaching',
        description: errorMessage,
      })
    },
  })

  const startBarging = useMutation({
    mutationFn: (payload: BargePayload) => conversationService.startBarging(payload),
    onSuccess: (_, { conversation_id }) => {
      setMonitoringState(MonitorStates.BARGE)
      updateActiveConversation({
        conversationNumber: conversation_id,
        data: { agent_secondary_conversation_state: MonitorStates.BARGE },
      })
    },
    onError: (e) => {
      const errorMessage = getErrorText(e as AxiosError)
      errorToast({
        title: 'Failed to barge-in',
        description: errorMessage,
      })
    },
  })

  const notifyMonitoring = useMutation({
    mutationFn: (conversationId: string) => executionService.notifyMonitoring(conversationId),
    onSuccess: (_, conversationId) => {
      setMonitoringState(MonitorStates.MONITOR_VISIBLE)
      updateActiveConversation({
        conversationNumber: conversationId,
        data: { agent_secondary_conversation_state: MonitorStates.MONITOR_VISIBLE },
      })
    },
  })

  const toggleSupervisorMute = useMutation({
    mutationFn: ({ conversationId, mute }: { conversationId: string; mute: boolean }) =>
      executionService.toggleSupervisorMute(conversationId, mute),
  })

  const requestHelp = useMutation({
    mutationFn: (conversationId: string) => executionService.requestHelp(conversationId),
  })

  const rejectAssistance = useMutation({
    mutationFn: (conversationId: string) => executionService.rejectAssistance(conversationId),
    onSuccess: () => {
      setMonitoringState(MonitorStates.MONITOR_VISIBLE)
    },
  })

  const leaveMonitoring = useMutation({
    mutationKey: conversationMutationKeys().leaveMonitoring,
    mutationFn: ({
      conversationId,
      state,
      agent_state,
    }: {
      conversationId: string
      state: ConversationStatus
      agent_state: MonitorStates
    }) => executionService.leaveMonitoring(conversationId, { state, agent_state }),
    onSuccess: (_, { conversationId }) => {
      setMonitoringState(null)
      primaryEventBus.broadcastToAll(PrimaryEvents.HANGUP_CALL)
      removeActiveConversation(conversationId)
      setIsMonitorInitiator(false)
    },
  })

  return {
    startMonitoring,
    startCoaching,
    startBarging,
    notifyMonitoring,
    toggleSupervisorMute,
    requestHelp,
    rejectAssistance,
    leaveMonitoring,
  }
}
